import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Ban,
  CheckCircle2,
  KeyRound,
  LockOpen,
  LogOut,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import { canManageRole, type UserListItem } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { RoleBadge } from '@/components/RoleBadge';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usersApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import { useAuthStore, useCan } from '@/features/auth/store';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { UserFormDialog } from './UserFormDialog';

type Confirm = {
  kind: 'delete' | 'sessions' | 'deactivate' | 'activate';
  user: UserListItem;
} | null;

const CONFIRM_TEXT: Record<
  NonNullable<Confirm>['kind'],
  { title: string; body: string; action: string }
> = {
  delete: {
    title: 'Excluir usuário?',
    body: 'O acesso é removido na hora. O histórico dele na auditoria é preservado. Prefira desativar se ele puder voltar.',
    action: 'Excluir',
  },
  sessions: {
    title: 'Encerrar todas as sessões?',
    body: 'O usuário será desconectado de todos os dispositivos e precisará entrar de novo.',
    action: 'Encerrar sessões',
  },
  deactivate: {
    title: 'Desativar usuário?',
    body: 'Ele perde o acesso imediatamente e as sessões abertas são encerradas. Pode ser reativado depois.',
    action: 'Desativar',
  },
  activate: {
    title: 'Reativar usuário?',
    body: 'Ele volta a conseguir entrar com a senha atual.',
    action: 'Reativar',
  },
};

export function UsersPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const me = useAuthStore((s) => s.user);
  const canManage = useCan('users.manage');

  const q = useQuery({
    queryKey: ['users', search],
    queryFn: () => usersApi.list(search || undefined),
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserListItem | undefined>();
  const [resetUser, setResetUser] = useState<UserListItem | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'] });
  const onError = (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'Erro');
  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      usersApi.update(id, { active }),
    onSuccess: (_, v) => {
      toast.success(v.active ? 'Usuário reativado' : 'Usuário desativado');
      invalidate();
    },
    onError,
  });
  const unlock = useMutation({
    mutationFn: usersApi.unlock,
    onSuccess: () => {
      toast.success('Conta desbloqueada');
      invalidate();
    },
    onError,
  });
  const revokeSessions = useMutation({
    mutationFn: usersApi.revokeSessions,
    onSuccess: ({ revoked }) => {
      toast.success(revoked ? `${revoked} sessão(ões) encerrada(s)` : 'Nenhuma sessão ativa');
      invalidate();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: usersApi.remove,
    onSuccess: () => {
      toast.success('Usuário excluído');
      invalidate();
    },
    onError,
  });

  const runConfirm = () => {
    if (!confirm) return;
    const { kind, user } = confirm;
    if (kind === 'delete') remove.mutate(user.id);
    if (kind === 'sessions') revokeSessions.mutate(user.id);
    if (kind === 'deactivate') setActive.mutate({ id: user.id, active: false });
    if (kind === 'activate') setActive.mutate({ id: user.id, active: true });
    setConfirm(null);
  };

  // Espelha as regras da API: cada perfil só é tocado por quem o alcança; ninguém se desativa/exclui.
  const locked = (u: UserListItem) => !me || !canManageRole(me.role, u.role);
  const isSelf = (u: UserListItem) => u.id === me?.id;
  const openEdit = (u: UserListItem) => {
    setEditing(u);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Usuários"
        description="Quem acessa o painel, com qual perfil, e as sessões abertas de cada um"
        actions={
          canManage && (
            <Button
              onClick={() => {
                setEditing(undefined);
                setFormOpen(true);
              }}
            >
              <Plus /> Novo usuário
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Nome ou e-mail"
            className="pl-8"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const v = (e.target as HTMLInputElement).value.trim();
              setParams(v ? { search: v } : {}, { replace: true });
            }}
          />
        </div>
        {q.data && (
          <span className="text-sm text-muted-foreground">
            {q.data.length} usuário(s) · {q.data.filter((u) => u.active).length} ativo(s)
          </span>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : q.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuário</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead className="text-center">Sessões</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.map((u) => (
                <TableRow key={u.id} className={u.active ? undefined : 'opacity-60'}>
                  <TableCell>
                    <p className="font-medium">
                      {u.name}
                      {isSelf(u) && (
                        <span className="ml-2 text-xs text-muted-foreground">(você)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={u.role} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {!u.active ? (
                        <StatusBadge label="Inativo" className="bg-muted text-muted-foreground" />
                      ) : u.lockedUntil ? (
                        <StatusBadge
                          label={`Bloqueado até ${fmtDateTime(u.lockedUntil)}`}
                          className="bg-destructive/10 text-destructive"
                        />
                      ) : (
                        <StatusBadge
                          label="Ativo"
                          className="bg-success/15 text-green-700 dark:text-green-300"
                        />
                      )}
                      {u.mustChangePassword && u.active && (
                        <Badge
                          variant="outline"
                          title="Vai definir uma nova senha no próximo acesso"
                        >
                          Senha temporária
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-muted-foreground"
                    title={fmtDateTime(u.lastLoginAt)}
                  >
                    {u.lastLoginAt ? fmtRelative(u.lastLoginAt) : 'Nunca entrou'}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">{u.activeSessions}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {fmtDateTime(u.createdAt)}
                  </TableCell>
                  <TableCell>
                    {canManage && !locked(u) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-8"
                            aria-label={`Ações de ${u.name}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)}>
                            <Pencil /> Editar
                          </DropdownMenuItem>
                          {!isSelf(u) && (
                            <DropdownMenuItem onClick={() => setResetUser(u)}>
                              <KeyRound /> Redefinir senha
                            </DropdownMenuItem>
                          )}
                          {u.lockedUntil && (
                            <DropdownMenuItem onClick={() => unlock.mutate(u.id)}>
                              <LockOpen /> Desbloquear
                            </DropdownMenuItem>
                          )}
                          {!isSelf(u) && u.activeSessions > 0 && (
                            <DropdownMenuItem
                              onClick={() => setConfirm({ kind: 'sessions', user: u })}
                            >
                              <LogOut /> Encerrar sessões
                            </DropdownMenuItem>
                          )}
                          {!isSelf(u) && (
                            <>
                              <DropdownMenuSeparator />
                              {u.active ? (
                                <DropdownMenuItem
                                  onClick={() => setConfirm({ kind: 'deactivate', user: u })}
                                >
                                  <Ban /> Desativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => setConfirm({ kind: 'activate', user: u })}
                                >
                                  <CheckCircle2 /> Reativar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setConfirm({ kind: 'delete', user: u })}
                              >
                                <Trash2 /> Excluir
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={UserCog}
              title="Nenhum usuário encontrado"
              description={search ? 'Tente outro termo de busca.' : 'Crie o primeiro usuário.'}
            />
          </div>
        )}
      </div>

      <UserFormDialog open={formOpen} onOpenChange={setFormOpen} user={editing} />
      <ResetPasswordDialog user={resetUser} onOpenChange={(o) => !o && setResetUser(null)} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm && CONFIRM_TEXT[confirm.kind].title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && (
                <>
                  <span className="font-medium text-foreground">
                    {confirm.user.name} ({confirm.user.email})
                  </span>
                  <br />
                  {CONFIRM_TEXT[confirm.kind].body}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={runConfirm}
              className={
                confirm?.kind === 'delete'
                  ? 'bg-destructive text-white hover:bg-destructive/90'
                  : undefined
              }
            >
              {confirm && CONFIRM_TEXT[confirm.kind].action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
